import React, { useState, useEffect, ChangeEvent, FormEvent } from "react";
import { useParams, useHistory } from "react-router-dom";
import { toast } from "react-toastify";

// Services & Types
import { EventService } from "services/event.service";
import { EventTypeService } from "services/event-type.service";
import {
  ISaveEvent,
  IUpdateEvent,
  IEventTypeResponse,
} from "models/church.types";

interface FormErrors {
  title?: string;
  eventDate?: string;
  eventTypeId?: string;
}

export default function EventRegister() {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const isEditMode = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [eventTypes, setEventTypes] = useState<IEventTypeResponse[]>([]);
  const [errors, setErrors] = useState<FormErrors>({});

  const [event, setEvent] = useState<ISaveEvent | IUpdateEvent>({
    title: "",
    eventDate: "",
    description: "",
    eventTypeId: 0,
  });

  // Charger la liste simple des types d'événements pour le select et initialiser le formulaire si mode édition
  useEffect(() => {
    const initData = async () => {
      try {
        // Chargement des types disponibles pour la liste déroulante
        const typesList = await EventTypeService.getAllSimple();
        setEventTypes(typesList);

        if (isEditMode && id) {
          const data = await EventService.getById(parseInt(id));

          // Conversion du format Instant (ISO) du backend vers le format datetime-local du HTML (YYYY-MM-DDTHH:mm)
          const formattedDate = data.eventDate
            ? data.eventDate.substring(0, 16)
            : "";

          setEvent({
            id: data.id,
            title: data.title || "",
            eventDate: formattedDate,
            description: data.description || "",
            eventTypeId: data.eventTypeId,
          });
        }
      } catch (error) {
        console.error("Erreur d'initialisation du composant :", error);
        toast.error(
          "Erreur lors de la récupération des données de configuration",
        );
        history.push("/admin/events");
      }
    };
    initData();
  }, [id, isEditMode, history]);

  const validate = (): boolean => {
    const newErrors: FormErrors = {};
    if (!event.title?.trim()) {
      newErrors.title = "Le titre de l'événement est obligatoire";
    }
    if (!event.eventDate) {
      newErrors.eventDate = "La date et l'heure sont obligatoires";
    }
    if (!event.eventTypeId || event.eventTypeId === 0) {
      newErrors.eventTypeId = "Le type d'événement est obligatoire";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;

    setEvent((prev) => ({
      ...prev,
      [name]: name === "eventTypeId" ? parseInt(value) || 0 : value,
    }));

    if (errors.hasOwnProperty(name)) {
      setErrors({ ...errors, [name]: undefined });
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      // Transformation de la date locale en format ISO avant l'envoi au backend (Spring Instant)
      const payload = {
        ...event,
        eventDate: new Date(event.eventDate).toISOString(),
      };

      if (isEditMode) {
        await EventService.update(payload as IUpdateEvent);
        toast.success("Événement mis à jour avec succès");
      } else {
        await EventService.create(payload as ISaveEvent);
        toast.success("Événement planifié avec succès");
      }
      history.push("/admin/events");
    } catch (error) {
      console.error("Erreur d'enregistrement :", error);
      toast.error("Erreur lors de l'enregistrement de l'événement.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center p-4 w-full min-h-screen">
      <div className="w-full max-w-2xl">
        <div className="relative flex flex-col min-w-0 break-words w-full mb-6 rounded-xl bg-[#1E293B] border border-slate-700 shadow-2xl">
          {/* Header */}
          <div className="rounded-t-xl bg-[#0F172A] px-6 py-4 border-b border-slate-700">
            <div className="flex justify-between items-center">
              <h6 className="text-white text-sm font-bold uppercase tracking-widest">
                <i className="fas fa-calendar-check mr-2 text-[#D4AF37]"></i>
                {isEditMode ? "Modifier l'événement" : "Planifier un événement"}
              </h6>
              <button
                type="button"
                onClick={() => history.goBack()}
                className="text-slate-400 hover:text-white text-xs font-bold uppercase transition-all"
              >
                <i className="fas fa-times mr-1"></i> Fermer
              </button>
            </div>
          </div>

          <div className="flex-auto px-6 py-8">
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 gap-6">
                {/* Titre de l'événement */}
                <div>
                  <label className="text-[10px] text-[#D4AF37] uppercase font-black tracking-widest ml-1 block mb-2">
                    <i className="fas fa-heading mr-2"></i>Titre de l'événement
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={event.title || ""}
                    onChange={handleChange}
                    placeholder="Ex: Grand Culte de Moisson, Nuit de Traversée..."
                    className={`w-full bg-[#0F172A] border ${
                      errors.title ? "border-red-500" : "border-slate-700"
                    } rounded-lg px-4 py-3 text-white text-sm focus:border-[#D4AF37] outline-none transition-all`}
                  />
                  {errors.title && (
                    <p className="text-red-500 text-[10px] mt-1 italic">
                      {errors.title}
                    </p>
                  )}
                </div>

                {/* Grid Type + Date */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Type d'événement (Dictionnaire lié) */}
                  <div>
                    <label className="text-[10px] text-[#D4AF37] uppercase font-black tracking-widest ml-1 block mb-2">
                      <i className="fas fa-layer-group mr-2"></i>Type
                      d'événement
                    </label>
                    <select
                      name="eventTypeId"
                      value={event.eventTypeId}
                      onChange={handleChange}
                      className={`w-full bg-[#0F172A] border ${
                        errors.eventTypeId
                          ? "border-red-500"
                          : "border-slate-700"
                      } rounded-lg px-4 py-3 text-white text-sm focus:border-[#D4AF37] outline-none transition-all color-scheme-dark`}
                    >
                      <option value={0}>-- Sélectionner un type --</option>
                      {eventTypes.map((type) => (
                        <option key={type.id} value={type.id}>
                          {type.name}
                        </option>
                      ))}
                    </select>
                    {errors.eventTypeId && (
                      <p className="text-red-500 text-[10px] mt-1 italic">
                        {errors.eventTypeId}
                      </p>
                    )}
                  </div>

                  {/* Date et Heure */}
                  <div>
                    <label className="text-[10px] text-[#D4AF37] uppercase font-black tracking-widest ml-1 block mb-2">
                      <i className="fas fa-clock mr-2"></i>Date et Heure
                    </label>
                    <input
                      type="datetime-local"
                      name="eventDate"
                      value={event.eventDate}
                      onChange={handleChange}
                      className={`w-full bg-[#0F172A] border ${
                        errors.eventDate ? "border-red-500" : "border-slate-700"
                      } rounded-lg px-4 py-3 text-white text-sm focus:border-[#D4AF37] outline-none transition-all color-scheme-dark`}
                    />
                    {errors.eventDate && (
                      <p className="text-red-500 text-[10px] mt-1 italic">
                        {errors.eventDate}
                      </p>
                    )}
                  </div>
                </div>

                {/* Description */}
                <div>
                  <label className="text-[10px] text-[#D4AF37] uppercase font-black tracking-widest ml-1 block mb-2">
                    <i className="fas fa-align-left mr-2"></i>Description /
                    Notes de planification
                  </label>
                  <textarea
                    name="description"
                    rows={4}
                    value={event.description || ""}
                    onChange={handleChange}
                    placeholder="Détails optionnels sur le programme, les orateurs ou l'organisation..."
                    className="w-full bg-[#0F172A] border border-slate-700 rounded-lg px-4 py-3 text-white text-sm focus:border-[#D4AF37] outline-none transition-all resize-none"
                  ></textarea>
                </div>
              </div>

              {/* Actions */}
              <div className="mt-10 flex justify-end gap-4">
                <button
                  type="button"
                  onClick={() => history.push("/admin/events")}
                  className="px-6 py-3 rounded-lg font-bold text-xs uppercase text-slate-400 hover:text-white transition-all"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="bg-[#D4AF37] text-[#0F172A] px-10 py-3 rounded-lg font-black text-xs uppercase tracking-widest hover:scale-105 transition-all shadow-lg disabled:opacity-50"
                >
                  {loading ? (
                    <i className="fas fa-spinner animate-spin mr-2"></i>
                  ) : (
                    <i className="fas fa-save mr-2"></i>
                  )}
                  {isEditMode ? "Mettre à jour" : "Planifier"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      <style>{`
        .color-scheme-dark {
          color-scheme: dark;
        }
      `}</style>
    </div>
  );
}
